import { useCallback, useEffect, useState } from "react";
import Button from "../../components/styled/Button";
import {
  FormContainer,
  InputGroup,
  FormLabel,
  Input,
  FormParagraph,
  FormHeadline,
} from "../../components/styled/Form";
import WalletForm from "../../components/WalletForm";
import InfoBlock from "./InfoBlock";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useWeb3React } from "@web3-react/core";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch, RootState } from "../../store";
import { TokenSubmissionContainer, WrappedRow } from "./styled";
import FormSteps from "../../components/FormSteps";
import { Divider } from "../../components/styled/Divider";
import { SnapshotSubmitForm } from "../../types";
import useSnapshotHistory from "../../hooks/useSnapshotHistory";
import SnapshotHistory from "../../components/SnapshotHistory";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorText, Paragraph } from "../../components/styled/Text";
import { TokenSubmissionSchema } from "./formValidationSchema";
import { Ripple } from "../../components/Loaders/Ripple";
import { ChainData, ALL_SUPPORTED_CHAIN_IDS } from "../../config/web3";

type FormData = {
  tokenAddress: string;
  toBlock: number;
};

const TokenSubmission = () => {
  const { snapshotHistory } = useSnapshotHistory();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onSubmit",
    resolver: yupResolver(TokenSubmissionSchema),
  });
  const navigator = useNavigate();
  const dispatch = useDispatch<Dispatch>();
  const { chainId, account } = useWeb3React();
  const snapshotError = useSelector(
    (state: RootState) => state.snapshotModel.snapshotError
  );
  const currentSnapshotId = useSelector(
    (state: RootState) => state.snapshotModel.currentSnapshotId
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // eslint-disable-next-line
  const onSubmitForm = useCallback(
    handleSubmit(async (formData) => {
      if (!account || !chainId) return;
      const dataWithChainId: SnapshotSubmitForm = {
        ...formData,
        chainId,
      };
      setIsSubmitting(true);
      await dispatch.snapshotModel.submitSnapshot(dataWithChainId);
      setIsSubmitting(false);
    }),
    [
      navigator,
      chainId,
      currentSnapshotId,
      account,
      dispatch.snapshotModel.submitSnapshot,
      errors,
      snapshotError,
    ]
  );

  useEffect(() => {
    dispatch.multistepModel.setCurrentStep(1);
    dispatch.snapshotModel.resetState();
    dispatch.disperse.resetState();
  }, [dispatch]);

  useEffect(() => {
    if (currentSnapshotId && chainId && account && !snapshotError) {
      navigator("/processing");
    }
  }, [currentSnapshotId, navigator, chainId, account, snapshotError]);
  return (
    <>
      <TokenSubmissionContainer>
        <InfoBlock />
        <WalletForm marginTop="50px" marginBottom="50px">
          <FormSteps />
          <Divider />
          <FormHeadline>Token Information</FormHeadline>
          <FormParagraph>
            Please indicate your desired token to relaunch details.
          </FormParagraph>
          <Paragraph>
            Supported chains:{" "}
            <strong>
              {ALL_SUPPORTED_CHAIN_IDS.map((chain) =>
                chain ===
                ALL_SUPPORTED_CHAIN_IDS[ALL_SUPPORTED_CHAIN_IDS.length - 1]
                  ? ChainData[chain].name
                  : ChainData[chain].name + ", "
              )}
            </strong>
          </Paragraph>
          <FormContainer
            style={{ display: "flex", flexDirection: "column" }}
            onSubmit={onSubmitForm}
          >
            <InputGroup>
              <FormLabel textAlign="left">Contract address</FormLabel>
              <Input {...register("tokenAddress")} />

              {errors.tokenAddress && (
                <ErrorText> {errors.tokenAddress.message} </ErrorText>
              )}
            </InputGroup>
            <InputGroup>
              <FormLabel>To Block number</FormLabel>
              <Input
                type="number"
                {...register("toBlock", {
                  valueAsNumber: true,
                })}
              />
              {errors.toBlock && (
                <ErrorText> {errors.toBlock.message} </ErrorText>
              )}
            </InputGroup>
            <WrappedRow>
              {snapshotError && <ErrorText> {snapshotError} </ErrorText>}
            </WrappedRow>
            <WrappedRow>
              {isSubmitting ? (
                <Ripple />
              ) : (
                <Button type="submit" disabled={isSubmitting}>
                  Submit
                </Button>
              )}
            </WrappedRow>
          </FormContainer>

          <SnapshotHistory historyData={snapshotHistory} />
        </WalletForm>
      </TokenSubmissionContainer>
    </>
  );
};

export default TokenSubmission;
